/**
 * Danh sách TỪNG KHÁCH đã làm trong tháng.
 *
 * Đây là mục CEO đặt hàng thẳng: "chụp 49 khách thì phải có thẻ hiển thị chi
 * tiết 49 khách". Nên bảng này liệt kê đủ, không cắt bớt, không phân trang —
 * ai đếm được 49 dòng thì người đó tin con số 49.
 */

import { useState } from "react";
import { Badge } from "@/ui/badge";
import { useProductNameMap } from "@/features/products/catalogApi";
import { vnd } from "../../constants";
import {
  groupOrderLines,
  isUnfinishedOrder,
  makeupHeadcountMismatchNote,
  orderLinePayNotes,
  priceTiers,
  shortDate,
  sortByShootDate,
  sumPay,
  type OrderLine,
} from "../../salaryDetail";
import {
  DetailSection,
  GroupHeading,
  LineOrderSwitch,
  LineTable,
  PayNotes,
  type LineOrder,
} from "./primitives";

/** Ngưỡng thu gọn: nhóm dài hơn thì cuộn dài quá, cho bấm mở. */
const COLLAPSE_AFTER = 12;

export function OrdersDetail({ lines }: { lines: OrderLine[] }) {
  // Mã gói → tên, từ danh mục một mối (bảng `products`). Máy chủ gắn MÃ vào
  // từng dòng bản kê ("at_makeup"), còn người đọc chỉ biết tên gói. Tra ở đây
  // chứ không đổi dữ liệu máy chủ: mã là thứ đối chiếu được với đơn, tên chỉ
  // để đọc. Bảng này đủ cả gói admin tự đặt (`sp_*`) lẫn gói đã ngừng bán —
  // hai thứ danh mục tĩnh trong mã nguồn không có.
  const productNames = useProductNameMap();
  const [order, setOrder] = useState<LineOrder>("date");

  if (!lines.length) return null;

  const groups = groupOrderLines(lines);
  // Chỉ một gói thì hai kiểu xếp ra cùng một bảng — không bày nút, giữ đầu
  // nhóm có đơn giá ("12 khách × 3 thạch lương thảo").
  const byDate = groups.length > 1 && order === "date";
  const totalPay = sumPay(lines);
  // Dòng máy CHƯA tính ra tiền được vì đơn còn thiếu thông tin (hay gặp nhất:
  // gói gia đình / bạn bè chưa khai số người trang điểm). Trước đây những dòng
  // này hiện 0đ trần trụi: thợ tưởng mình bị trừ, quản lý duyệt lướt qua,
  // không ai biết đơn còn thiếu gì — đúng cái "chốt vẫn trót lọt, thợ nhận 0đ
  // mà không ai biết".
  const blocked = lines.filter((l) => l.craftBlocked);
  // Dòng đang được TRẢ TẠM ở mức thấp nhất (quyết định 0173, CEO chốt câu 3c
  // 20/09/2026): có tiền, nhưng CHƯA phải số cuối. Để trần con số đó là thợ
  // tưởng buổi gia đình chỉ đáng từng ấy, quản lý tưởng bảng đã xong.
  const provisional = lines.filter((l) => l.craftProvisional);
  // Phòng nào không trả theo từng đơn (Sale) thì mọi dòng đều 0đ — lúc đó
  // hiện tổng 0đ cạnh tiêu đề là nói dối. Ẩn tổng đi và giải thích bằng chữ.
  //
  // ⚠️ Thợ trang điểm mà MỌI dòng đều bị chặn cũng ra tổng 0đ, nhưng câu giải
  // thích của Sale ("ăn hoa hồng trên tổng doanh số cả tháng") là SAI hoàn
  // toàn với họ. Có dòng bị chặn thì đây vẫn là phòng trả theo từng đơn.
  // Dòng "Chưa có mức trả" cũng vậy: đó là dòng của phòng TRẢ theo đơn mà gói
  // chưa được khai mức — tổng 0 ở đây không có nghĩa là ăn hoa hồng doanh số.
  // Dòng "Gói chưa xếp nhóm lương" (ảnh thẻ, T05 17/10/2026) cùng lý do.
  const pricedPerOrder =
    totalPay > 0 ||
    blocked.length > 0 ||
    provisional.length > 0 ||
    lines.some((l) => l.productRateMissing || l.productClassMissing);

  return (
    <DetailSection
      testId="detail-orders"
      icon="🧾"
      title="Khách đã làm"
      countLabel={`${lines.length} khách`}
      {...(pricedPerOrder ? { total: totalPay } : {})}
      note={
        blocked.length > 0 || provisional.length > 0 ? (
          <span className="text-amber-800">
            {provisional.length > 0 ? (
              <>
                <b>{provisional.length} đơn đang tạm tính</b> ở mức thấp nhất vì đơn còn thiếu thông
                tin. Bổ sung vào đơn rồi tính lại là ra số đúng — số đúng chỉ bằng hoặc cao hơn số
                tạm.{" "}
              </>
            ) : null}
            {blocked.length > 0 ? (
              <>
                <b>{blocked.length} đơn chưa ra tiền</b> vì đơn còn thiếu thông tin. Bổ sung vào đơn
                rồi tính lại là có tiền — <b>không phải bị trừ</b>.{" "}
              </>
            ) : null}
            Lý do ghi ngay dưới số tiền của từng dòng.
          </span>
        ) : pricedPerOrder ? undefined : (
          <>
            Phòng bạn ăn hoa hồng trên <b>tổng doanh số cả tháng</b>, không phải
            theo từng đơn, nên cột bên phải là doanh số của đơn chứ không phải
            tiền bạn nhận từ đơn đó.
          </>
        )
      }
    >
      {groups.length > 1 ? (
        <LineOrderSwitch value={order} onChange={setOrder} groupLabel="Theo gói" />
      ) : null}
      {byDate ? (
        // Liền một danh sách, không đầu nhóm: tên gói đã có ở cột Sản phẩm
        // (điện thoại thì hiện dưới tên khách, vì cột đó ẩn).
        <OrderGroup
          testId="orders-by-date"
          lines={sortByShootDate(lines)}
          pricedPerOrder={pricedPerOrder}
          productNames={productNames}
          productOnMobile
        />
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <OrderGroup
              key={g.key}
              heading={{ label: g.label, count: g.count, pay: g.pay }}
              testId={`order-group-${g.label}`}
              lines={g.lines}
              pricedPerOrder={pricedPerOrder}
              productNames={productNames}
            />
          ))}
        </div>
      )}
    </DetailSection>
  );
}

/**
 * Tên gói của dòng. Tra không ra thì hiện MÃ THÔ, không hiện gạch trống: mã
 * xấu nhưng tra lại được đơn, còn ô trống thì không nói gì.
 */
function productNameOf(line: OrderLine, productNames: Record<string, string>): string {
  return line.product ? (productNames[line.product] ?? line.product) : "—";
}

function OrderGroup({
  heading,
  testId,
  lines,
  pricedPerOrder,
  productNames,
  productOnMobile = false,
}: {
  /** Vắng mặt = danh sách "Theo ngày", không có đầu nhóm. */
  heading?: { label: string; count: number; pay: number } | undefined;
  testId: string;
  lines: OrderLine[];
  pricedPerOrder: boolean;
  productNames: Record<string, string>;
  productOnMobile?: boolean | undefined;
}) {
  // Nhóm có dòng chưa ra tiền thì MỞ SẴN. Thu gọn nó lại là giấu đúng thứ
  // người ta cần thấy: dải trên đầu báo "3 đơn chưa ra tiền" mà bấm vào nhóm
  // chỉ thấy 12 dòng đầu bình thường, hai bên trông như mâu thuẫn nhau.
  const [expanded, setExpanded] = useState(() =>
    lines.some((l) => l.craftBlocked || l.craftProvisional),
  );
  const hidden = Math.max(0, lines.length - COLLAPSE_AFTER);
  const shown = expanded ? lines : lines.slice(0, COLLAPSE_AFTER);
  // Mỗi dòng là một khách, tiền dòng là tiền của khách đó.
  const tiers = heading && pricedPerOrder
    ? priceTiers(lines.map((l) => ({ qty: 1, pay: l.pay ?? 0 })))
    : null;

  return (
    <div data-testid={testId}>
      {heading ? (
        <GroupHeading
          label={heading.label}
          count={heading.count}
          pay={pricedPerOrder ? heading.pay : undefined}
          tiers={tiers}
          noun="khách"
        />
      ) : null}

      <LineTable
        columns={[
          { key: "date", label: "Ngày" },
          { key: "code", label: "Mã đơn", className: "hidden md:table-cell" },
          { key: "customer", label: "Khách" },
          { key: "product", label: "Sản phẩm", className: "hidden md:table-cell" },
          { key: "pay", label: pricedPerOrder ? "Tiền" : "Doanh số", className: "text-right" },
        ]}
      >
        {shown.map((line, i) => (
          <OrderRow
            key={`${line.orderId}-${i}`}
            line={line}
            pricedPerOrder={pricedPerOrder}
            productName={productNameOf(line, productNames)}
            productOnMobile={productOnMobile}
          />
        ))}
      </LineTable>

      {hidden > 0 && !expanded ? (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
          onClick={() => setExpanded(true)}
        >
          Xem thêm {hidden} khách nữa
        </button>
      ) : null}
    </div>
  );
}

function OrderRow({
  line,
  pricedPerOrder,
  productName,
  productOnMobile,
}: {
  line: OrderLine;
  pricedPerOrder: boolean;
  productName: string;
  productOnMobile: boolean;
}) {
  // Đơn còn dở vẫn ĐƯỢC TÍNH TIỀN — nhãn chỉ để người đọc biết đường
  // hỏi lại, không phải dấu hiệu bị trừ. Đừng đổi thành gạch ngang
  // hay làm mờ số tiền: nhìn như bị trừ là đúng thứ cần tránh.
  const unfinished = isUnfinishedOrder(line.status);
  const payNotes = orderLinePayNotes(line);
  const headMismatch = makeupHeadcountMismatchNote(line);
  return (
    <tr className={unfinished ? "bg-amber-50 text-amber-950" : undefined}>
      <td className="whitespace-nowrap px-1 py-1.5 text-muted-foreground md:px-2">
        {shortDate(line.shootDate)}
      </td>
      <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
        {line.code || "—"}
      </td>
      <td className="px-1 py-1.5 md:px-2">
        {line.customer || "—"}
        {unfinished ? (
          <Badge
            variant="yellow"
            className="ml-1.5 shrink-0 px-1.5 py-0 text-[10px] align-middle"
          >
            Chưa hoàn thành
          </Badge>
        ) : null}
        {headMismatch ? (
          <Badge
            variant="red"
            title={headMismatch}
            className="ml-1.5 shrink-0 px-1.5 py-0 text-[10px] align-middle"
          >
            Lệch số người
          </Badge>
        ) : null}
        {headMismatch ? (
          <span className="block text-xs text-danger-text">{headMismatch}</span>
        ) : null}
        {productOnMobile ? (
          <span className="block text-xs text-muted-foreground md:hidden">
            {productName}
          </span>
        ) : null}
      </td>
      <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
        {productName}
      </td>
      <td className="px-1 py-1.5 text-right tabular-nums md:px-2">
        {vnd(pricedPerOrder ? (line.pay ?? 0) : (line.amount ?? 0))}
        {/* Bậc tay nghề đã dùng cho dòng này. Vắng mặt khi cơ chế
            chưa chạm vào dòng — hôm nay là mọi dòng — nên bảng
            không đổi gì cho tới ngày bật công tắc. */}
        <PayNotes
          notes={payNotes}
          blocked={line.craftBlocked || line.craftProvisional}
        />
      </td>
    </tr>
  );
}
